/* tslint:disable no-any */

declare const process: {
    env: {
        NODE_ENV: string;
    };
};

export interface AnyAction {
    type: any;
}

export interface Action<P> extends AnyAction {
    type: string;
    payload: P;
    error?: boolean;
    meta?: Object | null;
}

export interface Success<P, S> {
    params: P;
    result: S;
}

export interface Failure<P, E> {
    params: P;
    error: E;
}

export function isType<P>(
    action: AnyAction,
    actionCreator: ActionCreator<P>
): action is Action<P> {
    return action.type === actionCreator.type;
}

export interface ActionCreator<P> {
    type: string;
    (payload: P, meta?: Object | null): Action<P>;
}

export interface EmptyActionCreator extends ActionCreator<undefined> {
    (payload?: undefined, meta?: Object | null): Action<undefined>;
}

export interface CancellableActionCreator<P, S, E, C> {
    (payload: P, meta?: Object | null): Action<P>;

    type: string;
    done: ActionCreator<S>;
    failed: ActionCreator<E>;
    cancelled: ActionCreator<C>;
}

export interface ActionCreator0
    extends CancellableActionCreator<
        undefined,
        undefined,
        undefined,
        undefined
    > {
    (payload?: undefined, meta?: Object | null): Action<undefined>;
    done: EmptyActionCreator;
    failed: EmptyActionCreator;
    cancelled: EmptyActionCreator;
}

export interface ActionCreator1<P>
    extends CancellableActionCreator<P, undefined, undefined, undefined> {
    done: EmptyActionCreator;
    failed: EmptyActionCreator;
    cancelled: EmptyActionCreator;
}

export interface ActionCreator2<P, S>
    extends CancellableActionCreator<P, S, undefined, undefined> {
    failed: EmptyActionCreator;
    cancelled: EmptyActionCreator;
}

export interface ActionCreator3<P, S, E>
    extends CancellableActionCreator<P, S, E, undefined> {
    cancelled: EmptyActionCreator;
}

export interface ActionCreator4<P, S, E, C>
    extends CancellableActionCreator<P, S, E, C> {}

interface ActionFactory {
    (type: string, commonMeta?: Object | null): ActionCreator0;

    /**
     *
     */
    <P>(type: string, commonMeta?: Object | null): ActionCreator1<P>;

    <P, S>(type: string, commonMeta?: Object | null): ActionCreator2<P, S>;

    /**
     * First parameter is the initla,
     * next is success,
     * then error
     */
    <P, S, E>(type: string, commonMeta?: Object | null): ActionCreator3<
        P,
        S,
        E
    >;

    <P, S, E, C>(type: string, commonMeta?: Object | null): ActionCreator4<
        P,
        S,
        E,
        C
    >;

    cancellable(type: string, commonMeta?: Object | null): ActionCreator0;

    cancellable<P>(type: string, commonMeta?: Object | null): ActionCreator1<P>;

    cancellable<P, S>(
        type: string,
        commonMeta?: Object | null
    ): ActionCreator2<P, S>;

    cancellable<P, S, E>(
        type: string,
        commonMeta?: Object | null
    ): ActionCreator3<P, S, E>;

    cancellable<P, S, E, C>(
        type: string,
        commonMeta?: Object | null
    ): ActionCreator4<P, S, E, C>;
}

/**
 * import actionCreatorFactory from 'factory';
 *
 * const actionCreator = actionCreatorFactory('PREFIX');
 * const savePlan = actionCreator<string, errorType, cancelType>('SAVE_PLAN');
 *
 * dispatch(savePlan('params'))
 * dispatch(savePlan.done('params'))
 * dispatch(savePlan.failed('params'))
 * dispatch(savePlan.cancelled('params'))
 */
export default function factory(
    prefix?: string | null,
    defaultIsError: (payload: any) => boolean = (p) => p instanceof Error
): ActionFactory {
    const actionTypes: { [type: string]: boolean } = {};

    const base = prefix ? `${prefix}/` : "";

    function actionCreator<P>(
        type: string,
        commonMeta?: Object | null,
        isError: ((payload: P) => boolean) | boolean = defaultIsError
    ): ActionCreator<P> {
        const fullType = base + type;

        if (process.env.NODE_ENV !== "production") {
            if (actionTypes[fullType]) {
                throw new Error(`Duplicate action type: ${fullType}`);
            }

            actionTypes[fullType] = true;
        }

        return Object.assign(
            (payload: P, meta?: Object | null) => {
                const action: Action<P> = {
                    type: fullType,
                    payload,
                };
                if (typeof payload === "undefined" || payload === null) {
                    delete action.payload;
                }

                if (commonMeta || meta) {
                    action.meta = Object.assign({}, commonMeta, meta);
                }

                if (
                    isError &&
                    (typeof isError === "boolean" || isError(payload))
                ) {
                    action.error = true;
                }

                return action;
            },
            { type: fullType }
        );
    }

    function asyncActionCreators<P, S, E, C>(
        type: string,
        commonMeta?: Object | null
    ): CancellableActionCreator<P, S, E, C> {
        let p = actionCreator<P>(type, commonMeta, false);

        return Object.assign(p, {
            done: actionCreator<S>(`${type}_DONE`, commonMeta, false),
            failed: actionCreator<E>(`${type}_FAILED`, commonMeta, true),
            cancelled: actionCreator<C>(`${type}_CANCELLED`, commonMeta, false),
        });
    }

    /*
    return Object.assign(actionCreator, {
        async: asyncActionCreators,
        cancellable: asyncActionCreators,
    }) as any;
    */
    return Object.assign(asyncActionCreators, {
        cancellable: asyncActionCreators,
    });
}



// WEBPACK FOOTER //
// ./src/util/redux/action-factory.ts